import { useEffect, useRef, useState } from 'react';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Link,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import useSelfieCamera from '../../../hooks/useSelfieCamera';

const MAX_CNH_BYTES = 10 * 1024 * 1024;

const actionButtonSx = {
  textTransform: 'uppercase',
  fontWeight: 700,
  fontSize: 14,
  boxShadow: 'none',
  borderRadius: '4px',
  py: 1.25,
  '&:hover': { boxShadow: 'none' },
};

const dropzoneSx = {
  minHeight: 160,
  border: '1px dashed #D0D0D0',
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  px: 2,
  py: 3,
  overflow: 'hidden',
  textAlign: 'center',
};

function showSupport() {
  if (typeof window.Intercom === 'function') window.Intercom('show');
}

export default function ManualValidationDialog({
  open,
  onClose,
  onSave,
  isSaving,
}) {
  const { t } = useTranslation(['title', 'description', 'button']);
  const cnhInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [driverLicense, setDriverLicense] = useState(null);
  const [cnhError, setCnhError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCameraSession, setIsCameraSession] = useState(false);
  const {
    videoRef,
    isStarting,
    isCameraReady,
    cameraError,
    startCamera,
    stopCamera,
    capturePhoto,
  } = useSelfieCamera({ enabled: open && isCameraSession && !preview });

  useEffect(() => {
    if (open) return undefined;

    setPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    setSelfie(null);
    setDriverLicense(null);
    setCnhError('');
    setIsCameraSession(false);
    return undefined;
  }, [open]);

  const capture = async () => {
    setIsCapturing(true);
    const nextFile = await capturePhoto();
    if (nextFile) {
      setSelfie(nextFile);
      setPreview((current) => {
        if (current) URL.revokeObjectURL(current);
        return URL.createObjectURL(nextFile);
      });
      stopCamera();
      setIsCameraSession(false);
    }
    setIsCapturing(false);
  };

  const retake = () => {
    setPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    setSelfie(null);
    setIsCameraSession(false);
    stopCamera();
  };

  const handleCaptureLink = () => {
    if (cameraError) {
      startCamera();
      return;
    }
    if (isCameraReady) {
      capture();
      return;
    }
    setIsCameraSession(true);
  };

  const handleCnhChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    if (file && file.size > MAX_CNH_BYTES) {
      setDriverLicense(null);
      setCnhError(
        t('routes.identity_validation.cnh_too_large', { ns: 'description' })
      );
      return;
    }
    setCnhError('');
    setDriverLicense(file);
  };

  const busy = isStarting || isCapturing || isSaving;
  const hasCameraError = Boolean(cameraError) && !preview;
  const showLiveCamera = isCameraSession && !preview && !hasCameraError;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '8px',
          m: 2,
          width: 'calc(100% - 32px)',
          boxShadow: '0px 8px 24px rgba(33, 33, 33, 0.18)',
        },
      }}
    >
      <DialogContent sx={{ px: 3, pt: 4, pb: 3 }}>
        <Typography
          component="h2"
          sx={{
            fontSize: 20,
            fontWeight: 500,
            lineHeight: 1.3,
            color: '#212121',
            mb: 2,
          }}
        >
          {t('identity_validation.manual_title', { ns: 'title' })}
        </Typography>

        <Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 3 }}>
          {t('routes.identity_validation.manual_description', {
            ns: 'description',
          })}
        </Typography>

        <Box sx={{ ...dropzoneSx, py: preview || showLiveCamera ? 0 : 3 }}>
          {preview ? (
            <Box
              component="img"
              src={preview}
              alt={t('routes.identity_validation.selfie_alt', {
                ns: 'description',
              })}
              sx={{ width: '100%', height: 180, objectFit: 'cover' }}
            />
          ) : showLiveCamera ? (
            <Box sx={{ position: 'relative', width: '100%', minHeight: 180 }}>
              <Box
                component="video"
                ref={videoRef}
                autoPlay
                muted
                playsInline
                sx={{
                  width: '100%',
                  height: 180,
                  objectFit: 'cover',
                  transform: 'scaleX(-1)',
                  bgcolor: '#111',
                  display: 'block',
                }}
              />
              {(isStarting || !isCameraReady) && (
                <CircularProgress
                  size={28}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    mt: '-14px',
                    ml: '-14px',
                  }}
                />
              )}
            </Box>
          ) : (
            <CameraAltOutlinedIcon
              sx={{ fontSize: 28, color: 'primary.main' }}
            />
          )}

          {!preview && !showLiveCamera && (
            <>
              {hasCameraError && (
                <Typography sx={{ fontSize: 14, color: '#424242', px: 1 }}>
                  {t('routes.identity_validation.camera_permission_required', {
                    ns: 'description',
                  })}
                </Typography>
              )}
              <Link
                component="button"
                type="button"
                underline="always"
                onClick={handleCaptureLink}
                disabled={busy}
                sx={{ color: 'primary.main', fontSize: 14 }}
              >
                {t(
                  hasCameraError
                    ? 'identity_validation.retry_camera'
                    : 'identity_validation.take_photo',
                  { ns: 'button' }
                )}
              </Link>
            </>
          )}
        </Box>

        {preview && (
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Link
              component="button"
              type="button"
              underline="always"
              onClick={retake}
              disabled={busy}
              sx={{ color: 'primary.main', fontSize: 14 }}
            >
              {t('identity_validation.retake_photo', { ns: 'button' })}
            </Link>
          </Box>
        )}

        {showLiveCamera && (
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Link
              component="button"
              type="button"
              underline="always"
              onClick={capture}
              disabled={busy || !isCameraReady}
              sx={{ color: 'primary.main', fontSize: 14 }}
            >
              {t('identity_validation.capture_photo', { ns: 'button' })}
            </Link>
          </Box>
        )}

        <Box sx={{ ...dropzoneSx, mt: 2 }}>
          <UploadFileOutlinedIcon
            sx={{ fontSize: 28, color: 'primary.main' }}
          />
          <Link
            component="button"
            type="button"
            underline="always"
            onClick={() => cnhInputRef.current?.click()}
            disabled={busy}
            sx={{
              color: 'primary.main',
              fontSize: 14,
              maxWidth: 180,
              lineHeight: 1.3,
            }}
          >
            {t('routes.identity_validation.load_cnh', { ns: 'description' })}
          </Link>
          <Typography sx={{ fontSize: 13, color: '#757575' }}>
            {driverLicense?.name ||
              t('routes.identity_validation.cnh_formats', {
                ns: 'description',
              })}
          </Typography>
          <input
            ref={cnhInputRef}
            type="file"
            hidden
            accept="application/pdf,image/png,image/jpeg,image/jpg"
            onChange={handleCnhChange}
          />
        </Box>
        {cnhError && (
          <Typography sx={{ mt: 1, fontSize: 13, color: 'error.main' }}>
            {cnhError}
          </Typography>
        )}

        <Box sx={{ mt: 3 }}>
          <Button
            type="button"
            variant="contained"
            fullWidth
            disabled={!selfie || !driverLicense || busy}
            onClick={() => onSave({ selfie, driver_license: driverLicense })}
            sx={{
              ...actionButtonSx,
              '&.Mui-disabled': {
                backgroundColor: '#E6E6E6',
                color: '#9E9E9E',
              },
            }}
          >
            {isSaving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t('identity_validation.save', { ns: 'button' })
            )}
          </Button>
        </Box>

        <Box sx={{ borderBottom: '1px solid #E0E0E0', my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InfoOutlinedIcon sx={{ color: '#777777', fontSize: 22 }} />
          <Typography sx={{ fontSize: 14, color: '#6B6B6B', lineHeight: 1.4 }}>
            {t('routes.identity_validation.support_prefix', {
              ns: 'description',
            })}{' '}
            <Link
              component="button"
              type="button"
              underline="always"
              onClick={showSupport}
              sx={{ color: 'primary.main', fontSize: 'inherit' }}
            >
              {t('routes.identity_validation.support_link', {
                ns: 'description',
              })}
            </Link>
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
